// APL-11850 (Slice 5) — the v4 shell (spec § Frontend architecture — Shell
// fork + shared core). Mounted by src/index.tsx when `resolveUiV4()` is true.
import { DocumentTitleHandler, UnsavedChangesNotifier } from '@refinedev/react-router';
import React, { Suspense, useEffect } from 'react';
import { toast } from 'sonner';
import '../i18n';
import '../polyfills/regex-polyfill';
// ── v4 world ────────────────────────────────────────────────────────────────
// Tailwind (theme + utilities layers, NO preflight) enters the bundle HERE and
// only here — never from a v1 code path (plan § Cross-cutting — Styling).
import { DevToolsHotkey } from '@/components/ap/devtools/DevToolsDialog';
import { NetDebugPopover } from '@/components/ap/devtools/NetDebugPopover';
import { MaintenanceOverlay } from '@/components/ap/lockdown/MaintenanceOverlay';
import { Toaster } from '@/components/ui/sonner';
import { AppConfig } from '@/config/appConfig';
import { isProduction } from '@/lib/appConstants';
import { isBrowserSupported } from '@/lib/browserSupport';
import { initNetDebug } from '@/lib/netDebugLog';
import { registerV4AccommodationNotify } from '@/lib/notify/accommodationNotify';
import { registerApNotify } from '@/lib/notify/apNotify';
import { selectInitTheme, useThemeStore } from '@/lib/store/themeStore';
// Direct file import (not the pages/system barrel) — keeps the rest of the
// system cluster out of the boot chunk (Routes lazy-loads the barrel).
import { UnsupportedBrowserPage } from '@/pages/system/UnsupportedBrowserPage';
import { baseAxiosInstance } from '@/providers/dataProvider/utils/axios';
import { V4Routes } from '@/Routes';
import { useAuthStore } from '@/store';
import '@/styles/index.css';
import { getAppType } from '../lib/offline/OfflineAppHelper';
import { AppErrorBoundary } from './AppErrorBoundary';
import { AppProviders, AppRefine, registerShellNotificationHandler } from './Providers';
// CheckNativeAppUpdate must NOT be in the boot module graph: it statically
// pulls @/lib/offline/* whose Helper⇄Service import cycle only initializes
// safely when entered via the v1 entry order. Loading it lazily (post-boot
// microtask) keeps it out of the cycle entirely — its job is a background
// update check, so a one-tick delay is the v1-parity behavior anyway.
// (Regression: R14/DR6 static import → boot TDZ crash, dev report 2026-07-08.)
const CheckNativeAppUpdate = React.lazy(() =>
  import('@/components/ap/lockdown/CheckNativeAppUpdate').then((m) => ({
    default: m.CheckNativeAppUpdate,
  }))
);

// Internal ticket-builder — non-production only. Lazy + gated so the static
// Builder import never reaches the PROD bundle (SE1, 7.3b: the runtime
// !isProduction gate alone does not tree-shake a static import).
const DevBuilder = React.lazy(() =>
  Promise.all([
    import('@/components/ap/devtools/builder/Builder'),
    import('@/components/ap/devtools/builder/BuilderProvider'),
  ]).then(([builder, provider]) => {
    const { Builder } = builder;
    const { BuilderProvider } = provider;
    const Combined = () => (
      <BuilderProvider
        config={{
          apiUrl: AppConfig.aiServiceUrl,
          getAuthToken: () => useAuthStore.getState().token,
        }}
      >
        <Builder />
      </BuilderProvider>
    );
    return { default: Combined };
  })
);

// Sonner-backed side of the shared notification bridge (src/app/providers.tsx).
// Registered at module load: this module only ever loads when the v4 shell is
// active, so v1 users never pull sonner. Mirrors apMessageNotificationProvider
// type handling so Refine mutations toast identically in both shells.
registerShellNotificationHandler({
  open: ({ type, message: msg, description, key }) => {
    const content = description || msg;
    switch (type) {
      case 'success':
        toast.success(content, { id: key });
        break;
      case 'error':
        toast.error(content, { id: key });
        break;
      case 'warning':
        toast.warning(content, { id: key });
        break;
      case 'info':
      default:
        toast.info(content, { id: key });
    }
  },
  close: (key: string) => toast.dismiss(key),
});

// Sonner side of the imperative toast bridge (src/utils/apNotify.ts) — serves
// the antd `message`/`notification` statics converted out of shared files.
// antd duration is seconds (0 = sticky); sonner wants ms (Infinity = sticky).
const toSonnerDuration = (seconds?: number): number | undefined =>
  seconds === undefined ? undefined : seconds === 0 ? Infinity : seconds * 1000;

registerApNotify({
  message: {
    success: (content) => void toast.success(content),
    error: (content) => void toast.error(content),
    info: (content) => void toast.info(content),
    warning: (content) => void toast.warning(content),
    loading: (content) => void toast.loading(content),
    open: ({ type = 'info', content, key }) => void toast[type](content, { id: key }),
    destroy: (key) => toast.dismiss(key),
  },
  notification: {
    success: ({ message: msg, description, key, duration }) =>
      void toast.success(msg, { description, id: key, duration: toSonnerDuration(duration) }),
    error: ({ message: msg, description, key, duration }) =>
      void toast.error(msg, { description, id: key, duration: toSonnerDuration(duration) }),
    info: ({ message: msg, description, key, duration }) =>
      void toast.info(msg, { description, id: key, duration: toSonnerDuration(duration) }),
    warning: ({ message: msg, description, key, duration }) =>
      void toast.warning(msg, { description, id: key, duration: toSonnerDuration(duration) }),
    destroy: (key) => toast.dismiss(key),
  },
});

// Sonner side of the rich accommodation-notification bridge
// (src/utils/accommodationNotify.ts) — action buttons map to sonner actions,
// duration-0 stickiness maps to Infinity, destroy-by-key → toast.dismiss.
registerV4AccommodationNotify({
  show: ({ type, key, title, message, buttonText, onButtonClick }) =>
    void toast[type](title, {
      id: key,
      description: message,
      duration: Infinity,
      action: { label: buttonText, onClick: onButtonClick },
    }),
  showPreview: ({ key, title, message }) =>
    void toast.warning(title, { id: key, description: message, duration: 5000 }),
  dismiss: (key) => toast.dismiss(key),
});

// Apply the persisted/system theme before first paint (idempotent; the v1
// shell never calls this, so v3 users never get theme classes on <html>).
useThemeStore.getState().initTheme();

// Browser floor (THEME.md law 10) — evaluated once at boot; the CSS engine
// can't gain features mid-session.
const browserSupported = isBrowserSupported();

// iPad boot triage (APL-11850): network capture for the native iPad (ios)
// lockdown app on non-production envs only — everywhere else this is a no-op
// (nothing patched, nothing rendered). Module scope: patches before first
// render; getAppType is safe here because the offline module graph is fully
// initialized once App.tsx's imports have evaluated.
const netDebugEnabled = initNetDebug({
  isIosApp: getAppType() === 'ios',
  axiosInstance: baseAxiosInstance,
});

function App() {
  const initTheme = useThemeStore(selectInitTheme);
  const appType = getAppType();

  // Safety net for HMR/store-reset edge cases — initTheme is idempotent.
  useEffect(() => {
    initTheme();
  }, [initTheme]);

  // Same boot effects as AppV1: hide the index.html loading spinner and the
  // cookie-consent banner inside native apps.
  useEffect(() => {
    const loadingSpinner = document.getElementById('main-loading-spinner');
    if (loadingSpinner) {
      loadingSpinner.style.display = 'none';
    }
  }, []);

  useEffect(() => {
    const cookieConsent = document.querySelector('.cc-window') as HTMLDivElement;
    if (cookieConsent) {
      cookieConsent.style.display = appType !== 'web' ? 'none' : '';
    }
  }, [appType]);

  // Browser floor gate: below the floor the token/kit CSS renders broken —
  // show the update page INSTEAD of the provider/router tree (nothing
  // exam-critical ever mounts). Suspense covers the async i18n backend.
  if (!browserSupported) {
    return (
      // Hardcoded-English fallback on purpose: the gated audience (legacy
      // school machines, possibly slow networks) must never sit on a blank
      // page while the HTTP i18n backend loads the real translated notice.
      <Suspense
        fallback={
          <p className="p-8 text-center">
            This browser version cannot run AssessPrep. Please update your browser, or on a Mac
            update macOS to 12.3 or later.
          </p>
        }
      >
        <UnsupportedBrowserPage />
      </Suspense>
    );
  }

  // AppErrorBoundary is the OUTERMOST element: its fallback needs only the
  // module-level singletons App.tsx already imported (i18next instance, token
  // CSS, theme class on <html>) — no React provider — so sitting above
  // AppProviders (the BrowserRouter) makes a throw during routing catchable
  // too. Route-local boundaries (Take, Analyse) stay nearer and win.
  return (
    <>
      {/* Outside the error boundary AND the provider/suspense tree on purpose:
        the network-log chip must stay visible while boot is stuck on a
        skeleton or the app has crashed to the error fallback. */}
      {netDebugEnabled && <NetDebugPopover />}
      <AppErrorBoundary>
        <AppProviders>
          {/* v3 parity (DR6-lockdown-chrome): maintenance overlay wraps the Refine
          tree; the bare CheckNativeAppUpdate fires the Mac auto-update check on
          app boot. ExperienceRouteLayout carries the gated deep-route mount
          (exam-security-adjacent — real-device QA owed; MIGRATION § R15b-b3). */}
          <MaintenanceOverlay>
            <AppRefine>
              {/* Internal AI ticket-builder (Cmd/Ctrl+Shift+B), non-production only.
              v1-styled floating panel — acceptable for internal-only tooling. */}
              {!isProduction && (
                <Suspense fallback={null}>
                  <DevBuilder />
                </Suspense>
              )}
              {/* Alt/Cmd+K dev-tools dialog (DT1): features overrides + AI-model
              override tabs. Self-gated non-production only — one global mount
              reaches every route tree (in-shell AND the shell-less manage/take/
              print/embed trees). */}
              <DevToolsHotkey />
              <V4Routes />
              <Suspense fallback={null}>
                <CheckNativeAppUpdate />
              </Suspense>
              <UnsavedChangesNotifier />
              <DocumentTitleHandler
                handler={() => 'AssessPrep - Next Generation Assessment Platform'}
              />
            </AppRefine>
          </MaintenanceOverlay>
          <Toaster />
        </AppProviders>
      </AppErrorBoundary>
    </>
  );
}

export default App;
